import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, Calendar, AlertTriangle } from 'lucide-react';
import { FileText, Clock, User, Eye, Download, UserCheck, Search, ChevronDown } from './icons';
import { useNotificationContext } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useUserSubroles } from '../hooks/useUserSubroles';
import { pazYSalvoAPI } from '../services/api';
import PazYSalvoSignatureModal from './PazYSalvoSignatureModal';

export default function PazYSalvoRoleView({ userRole, showHeader = true }) {
    const [pazYSalvos, setPazYSalvos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPazYSalvo, setSelectedPazYSalvo] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [delegations, setDelegations] = useState([]);
    const [showDelegationModal, setShowDelegationModal] = useState(false);
    const [delegationUsers, setDelegationUsers] = useState([]);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [newDelegation, setNewDelegation] = useState({
        usuarioDelegadoId: '',
        motivo: '',
        fechaFin: ''
    });
    const [observations, setObservations] = useState('');
    const { alertSuccess, alertError } = useNotificationContext();
    const { user } = useAuth();
    const { getDelegations } = useUserSubroles();

    // Función para extraer userId del token JWT
    // Verificar si el usuario es delegado (no puede gestionar delegaciones)
    const isDelegatedUser = () => {
        // Admin siempre puede gestionar delegaciones
        if (user?.role === 'admin') {
            return false;
        }
        const delegations = getDelegations();
        return delegations && delegations.length > 0;
    };

    // Configuración específica por rol
    const roleConfig = {
        'JefeInmediato': {
            title: 'Paz y Salvo - Jefatura Directa',
            description: 'Revisar y aprobar solicitudes de Paz y Salvo de su equipo',
            canApprove: true,
            canReject: true,
            canView: true,
            canGenerate: false,
            filters: ['Pendiente', 'En Revisión'],
            checklistItems: [
                'Verificar que el empleado haya entregado todas las tareas asignadas',
                'Confirmar que no hay pendientes de trabajo en curso',
                'Revisar que se haya realizado la transición de responsabilidades',
                'Validar que no existan compromisos laborales pendientes'
            ],
            approvalText: 'Aprobar como Jefe Directo',
            rejectText: 'Rechazar - Hay pendientes laborales'
        },
        'Jefatura Directa': {
            title: 'Paz y Salvo - Jefatura Directa',
            description: 'Revisar y aprobar solicitudes de Paz y Salvo de su equipo',
            canApprove: true,
            canReject: true,
            canView: true,
            canGenerate: false,
            filters: ['Pendiente', 'En Revisión'],
            checklistItems: [
                'Verificar que el empleado haya entregado todas las tareas asignadas',
                'Confirmar que no hay pendientes de trabajo en curso',
                'Revisar que se haya realizado la transición de responsabilidades',
                'Validar que no existan compromisos laborales pendientes'
            ],
            approvalText: 'Aprobar como Jefe Directo',
            rejectText: 'Rechazar - Hay pendientes laborales'
        },
        'RRHH': {
            title: 'Paz y Salvo - Recursos Humanos',
            description: 'Gestionar y generar Paz y Salvo para todos los empleados',
            canApprove: true,
            canReject: true,
            canView: true,
            canGenerate: true,
            filters: ['Todos'],
            checklistItems: [
                'Revisar que todas las áreas hayan firmado',
                'Verificar documentación de nómina completa',
                'Confirmar liquidación de beneficios',
                'Validar entrega de elementos de la empresa'
            ],
            approvalText: 'Generar Paz y Salvo Final',
            rejectText: 'Rechazar - Faltan requisitos'
        },
        'TI': {
            title: 'Paz y Salvo - Tecnología',
            description: 'Revisar activos tecnológicos antes de la aprobación',
            canApprove: true,
            canReject: true,
            canView: true,
            canGenerate: false,
            filters: ['Pendiente', 'En Revisión'],
            checklistItems: [
                'Verificar devolución de equipos asignados (laptop, mouse, teclado)',
                'Confirmar entrega de credenciales y accesos',
                'Revisar que se hayan deshabilitado cuentas de usuario',
                'Validar devolución de licencias de software',
                'Confirmar backup de información importante'
            ],
            approvalText: 'Aprobar - Activos TI Entregados',
            rejectText: 'Rechazar - Faltan Activos TI'
        },
        'Contabilidad': {
            title: 'Paz y Salvo - Contabilidad',
            description: 'Verificar aspectos financieros y contables',
            canApprove: true,
            canReject: true,
            canView: true,
            canGenerate: false,
            filters: ['Pendiente', 'En Revisión'],
            checklistItems: [
                'Verificar liquidación de sueldos y beneficios',
                'Revisar descuentos pendientes (préstamos, anticipos)',
                'Confirmar devolución de viáticos no rendidos',
                'Validar que no hay deudas pendientes con la empresa',
                'Revisar estado de cuentas por cobrar/pagar'
            ],
            approvalText: 'Aprobar - Sin Deudas Pendientes',
            rejectText: 'Rechazar - Hay Deudas Pendientes'
        },
        'Gerencia Finanzas': {
            title: 'Paz y Salvo - Gerencia de Finanzas',
            description: 'Aprobación final de Paz y Salvo',
            canApprove: true,
            canReject: true,
            canView: true,
            canGenerate: false,
            filters: ['Pendiente', 'En Revisión'],
            checklistItems: [
                'Revisar que Contabilidad haya aprobado la liquidación',
                'Verificar que TI haya confirmado devolución de activos',
                'Confirmar que Jefatura Directa aprobó la salida',
                'Validar el cumplimiento de todos los requisitos financieros',
                'Autorizar el cierre definitivo del expediente'
            ],
            approvalText: 'Aprobación Final - Gerencia',
            rejectText: 'Rechazar - Revisar Requisitos'
        },
        'Gerente General': {
            title: 'Paz y Salvo - Gerencia General',
            description: 'Aprobación final de finiquitos (solo VICSA y B2B)',
            canApprove: true,
            canReject: true,
            canView: true,
            canGenerate: false,
            filters: ['Pendiente', 'En Revisión'],
            checklistItems: [
                'Verificar que todas las áreas hayan firmado el Paz y Salvo',
                'Confirmar que RRHH haya completado el proceso de cierre',
                'Revisar la documentación de liquidación de beneficios',
                'Validar que no existan pendientes administrativos',
                'Autorizar el pago del finiquito correspondiente'
            ],
            approvalText: 'Aprobar Finiquito - Gerencia General',
            rejectText: 'Rechazar - Revisar Requisitos'
        }
    };

    const config = roleConfig[userRole] || roleConfig['JefeInmediato'];

    const selectUser = (user) => {
        if (user && user.Id && user.DisplayName) {
            setNewDelegation({ ...newDelegation, usuarioDelegadoId: user.Id });
            setUserSearchTerm(user.DisplayName);
            setShowUserDropdown(false);
        }
    };

    const filteredUsers = delegationUsers.filter(user =>
        user &&
        user.DisplayName &&
        user.Email &&
        (user.DisplayName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
            user.Email.toLowerCase().includes(userSearchTerm.toLowerCase()))
    );

    useEffect(() => {
        let mounted = true;

        const loadData = async () => {
            if (!mounted) return;
            await loadPazYSalvos();
            if (mounted) await loadDelegations();
        };

        loadData();

        return () => { mounted = false; };
    }, [userRole]); // Removido showDelegationModal para evitar recargas innecesarias de todo

    // Efecto separado para cargar usuarios de delegación solo cuando se abre el modal
    useEffect(() => {
        if (showDelegationModal) {
            loadDelegationUsers();
        }
    }, [showDelegationModal]);

    // Manejar el scroll del body cuando el modal esté abierto
    useEffect(() => {
        if (showModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup function para restaurar el scroll cuando el componente se desmonte
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showModal]);

    // Cerrar dropdown cuando se hace clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
                setShowUserDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserDropdown]);

    const loadPazYSalvos = async () => {
        try {
            setLoading(true);

            // Si es admin o Gerente General, no aplicar filtros de usuario
            const params = (user?.role !== 'admin' && userRole !== 'Gerente General')
                ? { userId: user?.id }
                : {};

            const response = await pazYSalvoAPI.getAll(params);
            const data = response.data;

            // El backend devuelve una estructura paginada con 'items'
            let allPazYSalvos = [];
            if (data.items && Array.isArray(data.items)) {
                allPazYSalvos = data.items;
            } else if (Array.isArray(data)) {
                allPazYSalvos = data;
            } else {
                allPazYSalvos = [];
            }

            // Si es Gerente General, solo mostrar Paz y Salvo de VICSA y B2B
            const filteredPazYSalvos = userRole === 'Gerente General'
                ? allPazYSalvos.filter(ps => {
                    const empresa = (ps.usuarioEmpresa || '').toUpperCase();
                    return empresa.includes('VICSA') || empresa.includes('B2B') || empresa.includes('B2BWEB');
                })
                : allPazYSalvos;

            setPazYSalvos(filteredPazYSalvos);
        } catch (error) {
            console.error('Error al cargar Paz y Salvo:', error);
            alertError('Error al cargar los Paz y Salvo');
            setPazYSalvos([]);
        } finally {
            setLoading(false);
        }
    };

    const loadDelegations = async () => {
        try {
            const response = await pazYSalvoAPI.getDelegations();
            setDelegations(response.data || []);
        } catch (error) {
            console.error('Error al cargar delegaciones:', error);
        }
    };

    const loadDelegationUsers = async () => {
        try {
            const res = await pazYSalvoAPI.getUsers();
            const data = res.data || [];

            // Convertir la estructura para que sea compatible con el sistema de delegaciones
            const convertedUsers = data.map(user => ({
                Id: user.id,
                Nombre: user.nombre,
                Apellido: user.apellido,
                Email: user.email,
                Cargo: user.cargo || 'Usuario',
                Departamento: user.departamento || 'Sin departamento',
                Empresa: user.empresa || 'Sin empresa',
                DisplayName: `${user.nombre} ${user.apellido} (${user.email})`,
                hasAuthUser: user.hasAuthUser
            }));

            setDelegationUsers(convertedUsers);
        } catch (error) {
            console.error('Error al cargar usuarios para delegación:', error);
            console.error('Error completo:', error);
            alertError('Error al cargar usuarios disponibles');
        }
    };

    const handleCreateDelegation = async () => {
        if (!newDelegation.usuarioDelegadoId) {
            alertError('Por favor selecciona un usuario');
            return;
        }

        if (!newDelegation.motivo.trim()) {
            alertError('Por favor ingresa el motivo de la delegación');
            return;
        }

        if (!newDelegation.fechaFin) {
            alertError('Por favor selecciona la fecha de fin de la delegación');
            return;
        }

        try {
            const payload = {
                usuarioDelegadoId: parseInt(newDelegation.usuarioDelegadoId),
                motivo: newDelegation.motivo,
                subRole: userRole,
                fechaFin: new Date(newDelegation.fechaFin).toISOString()
            };



            const response = await pazYSalvoAPI.createDelegation(payload);

            alertSuccess('Delegación creada exitosamente');
            setShowDelegationModal(false);
            setNewDelegation({ usuarioDelegadoId: '', motivo: '', fechaFin: '' });
            setUserSearchTerm('');
            loadDelegations();
        } catch (error) {
            console.error('Error al crear delegación:', error);

            let errorMessage = 'Error al crear la delegación';

            if (error.response?.data) {
                if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data.errors) {
                    // Handle validation errors (e.g. from ModelState)
                    const errors = Object.values(error.response.data.errors).flat();
                    errorMessage = errors.join(', ');
                }
            }

            alertError(errorMessage);
        }
    };

    const handleRevokeDelegation = async (delegationId) => {
        try {
            await pazYSalvoAPI.revokeDelegation(delegationId);
            alertSuccess('Delegación revocada exitosamente');
            loadDelegations();
        } catch (error) {
            console.error('Error al revocar delegación:', error);
            const errorMessage = error.response?.data?.message || 'Error al revocar la delegación';
            alertError(errorMessage);
        }
    };

    const [previewLoading, setPreviewLoading] = useState({});

    const handlePrevisualizarDocumento = async (pazYSalvoId) => {
        try {
            setPreviewLoading(prev => ({ ...prev, [pazYSalvoId]: true }));

            const response = await pazYSalvoAPI.descargarPdf(pazYSalvoId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');

            // Limpiar el objeto URL después de un tiempo
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (error) {
            console.error('Error al previsualizar documento:', error);
            alertError('Error al generar la previsualización del documento');
        } finally {
            setPreviewLoading(prev => ({ ...prev, [pazYSalvoId]: false }));
        }
    };

    const getStatusColor = (estado) => {
        switch (estado) {
            case 'Pendiente': return 'bg-yellow-100 text-yellow-800';
            case 'En Revisión': return 'bg-blue-100 text-blue-800';
            case 'Aprobado': return 'bg-green-100 text-green-800';
            case 'Rechazado': return 'bg-red-100 text-red-800';
            case 'Completado': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (estado) => {
        switch (estado) {
            case 'Pendiente': return <Clock className="h-4 w-4" />;
            case 'En Revisión': return <Eye className="h-4 w-4" />;
            case 'Aprobado': return <CheckCircle className="h-4 w-4" />;
            case 'Rechazado': return <XCircle className="h-4 w-4" />;
            case 'Completado': return <CheckCircle className="h-4 w-4" />;
            default: return <AlertTriangle className="h-4 w-4" />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    // Si no hay configuración para el rol, mostrar mensaje de error
    if (!config) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center">
                    <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
                    <div>
                        <h3 className="text-lg font-medium text-red-900">
                            Configuración de rol no encontrada
                        </h3>
                        <p className="text-red-700 mt-1">
                            No se encontró configuración para el rol: "{userRole}"
                        </p>
                        <p className="text-red-600 text-sm mt-2">
                            Roles disponibles: {Object.keys(roleConfig).join(', ')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {showHeader && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center space-x-3">
                        <FileText className="h-8 w-8 text-blue-600" />
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {config.title}
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                {config.description}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Información específica del rol */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                        <div className="flex-shrink-0">
                            <CheckCircle className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-3">
                                Qué debe revisar como {userRole}:
                            </h3>
                            <ul className="space-y-2">
                                {config.checklistItems.map((item, index) => (
                                    <li key={index} className="flex items-start space-x-2">
                                        <div className="flex-shrink-0 mt-0.5">
                                            <div className="h-2 w-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                                        </div>
                                        <span className="text-sm text-blue-800 dark:text-blue-200">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    {/* Solo mostrar gestión de delegaciones si el usuario NO es delegado */}
                    {!isDelegatedUser() && (
                        <div className="flex-shrink-0 ml-4">
                            <button
                                onClick={() => setShowDelegationModal(true)}
                                className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-lg text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                            >
                                <UserCheck className="h-4 w-4 mr-2" />
                                Gestionar Delegaciones
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Filtros */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex flex-wrap gap-2">
                    {config.filters.map((filter) => (
                        <button
                            key={filter}
                            className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200 transition-colors"
                        >
                            {filter}
                        </button>
                    ))}
                </div>
            </div>

            {/* Lista de Paz y Salvo */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Solicitudes de Paz y Salvo
                    </h2>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Usuario
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Fecha Creación
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Motivo
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Progreso Firmas
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {pazYSalvos.map((pazYSalvo) => (
                                <tr key={pazYSalvo.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <User className="h-5 w-5 text-gray-400 mr-3" />
                                            <div>
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {pazYSalvo.usuarioNombre}
                                                </div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                    {pazYSalvo.usuarioRut}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pazYSalvo.estado)}`}>
                                            {getStatusIcon(pazYSalvo.estado)}
                                            <span className="ml-1">{pazYSalvo.estado}</span>
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        <div className="flex items-center">
                                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                            {new Date(pazYSalvo.fechaCreacion).toLocaleDateString()}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                        {pazYSalvo.motivoSalida || 'No especificado'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        <div className="flex flex-wrap gap-1">
                                            {pazYSalvo.estado === 'EnFirma' ? (
                                                <div className="flex flex-col space-y-1">
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        🔄 En proceso de firmas
                                                    </span>
                                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                                        {pazYSalvo.firmasTotales - pazYSalvo.firmasPendientes} / {pazYSalvo.firmasTotales} firmas completadas
                                                    </div>
                                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                                                        <div
                                                            className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                                                            style={{
                                                                width: `${pazYSalvo.firmasTotales > 0 ? ((pazYSalvo.firmasTotales - pazYSalvo.firmasPendientes) / pazYSalvo.firmasTotales) * 100 : 0}%`
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            ) : pazYSalvo.estado === 'Pendiente' ? (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    ⏳ Pendiente de envío
                                                </span>
                                            ) : pazYSalvo.estado === 'Aprobado' ? (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    ✅ Aprobado
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    📄 {pazYSalvo.estado}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-3">
                                            {config.canView && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedPazYSalvo(pazYSalvo);
                                                        setShowModal(true);
                                                    }}
                                                    className="inline-flex items-center px-4 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                                                >
                                                    <Eye className="h-4 w-4 mr-2" />
                                                    Ver Detalles
                                                </button>
                                            )}
                                            {/* Botón de Previsualizar Documento */}
                                            <button
                                                onClick={() => handlePrevisualizarDocumento(pazYSalvo.id)}
                                                disabled={previewLoading[pazYSalvo.id]}
                                                className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {previewLoading[pazYSalvo.id] ? (
                                                    <>
                                                        <div className="animate-spin rounded-full h-4 w-4 mr-1 border-2 border-blue-600 border-t-transparent"></div>
                                                        Generando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FileText className="h-4 w-4 mr-1" />
                                                        Previsualizar
                                                    </>
                                                )}
                                            </button>

                                            {pazYSalvo.pdfFinalPath && (
                                                <button
                                                    onClick={() => window.open(pazYSalvo.pdfFinalPath, '_blank')}
                                                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                                                >
                                                    <Download className="h-4 w-4 mr-1" />
                                                    PDF Final
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de firma con checklist */}
            <PazYSalvoSignatureModal
                pazYSalvo={selectedPazYSalvo}
                userRole={userRole}
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setSelectedPazYSalvo(null);
                }}
                onSuccess={() => {
                    loadPazYSalvos();
                    setShowModal(false);
                    setSelectedPazYSalvo(null);
                }}
                config={config}
            />

            {/* Modal de delegaciones */}
            {showDelegationModal && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowDelegationModal(false);
                        }
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    Gestionar Delegaciones - {userRole}
                                </h3>
                                <button
                                    onClick={() => setShowDelegationModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <XCircle className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Formulario para crear delegación */}
                            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                <h4 className="text-md font-medium text-blue-900 dark:text-blue-100 mb-4">
                                    Crear Nueva Delegación
                                </h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Usuario a Delegar
                                        </label>
                                        <div className="relative user-dropdown-container">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={userSearchTerm}
                                                    onChange={(e) => {
                                                        setUserSearchTerm(e.target.value);
                                                        setShowUserDropdown(true);
                                                        if (!e.target.value) {
                                                            setNewDelegation({ ...newDelegation, usuarioDelegadoId: '' });
                                                        }
                                                    }}
                                                    onFocus={() => setShowUserDropdown(true)}
                                                    placeholder="Buscar usuario..."
                                                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                                />
                                                <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                                            </div>

                                            {showUserDropdown && (
                                                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                                                    {filteredUsers.length > 0 ? (
                                                        filteredUsers.map((user) => (
                                                            user && user.Id ? (
                                                                <div
                                                                    key={user.Id}
                                                                    onClick={() => selectUser(user)}
                                                                    className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                                                                >
                                                                    <div className="font-medium text-gray-900 dark:text-white">
                                                                        {user.Nombre || 'Sin nombre'} {user.Apellido || 'Sin apellido'}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                        {user.Email || 'Sin email'}
                                                                    </div>
                                                                    <div className="text-xs text-gray-400 dark:text-gray-500">
                                                                        {user.Cargo || 'Sin cargo'} - {user.Departamento || 'Sin departamento'}
                                                                    </div>
                                                                </div>
                                                            ) : null
                                                        ))
                                                    ) : (
                                                        <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">
                                                            No se encontraron usuarios
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Motivo de Delegación
                                        </label>
                                        <textarea
                                            value={newDelegation.motivo}
                                            onChange={(e) => setNewDelegation({ ...newDelegation, motivo: e.target.value })}
                                            placeholder="Vacaciones, ausencia temporal, etc."
                                            rows={2}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Fecha Fin *
                                        </label>
                                        <input
                                            type="date"
                                            value={newDelegation.fechaFin}
                                            onChange={(e) => setNewDelegation({ ...newDelegation, fechaFin: e.target.value })}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                        />
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            La delegación expirará automáticamente en esta fecha
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleCreateDelegation}
                                        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                                    >
                                        Crear Delegación
                                    </button>
                                </div>
                            </div>

                            {/* Lista de delegaciones activas */}
                            <div>
                                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                                    Delegaciones Activas
                                </h4>
                                {delegations.length > 0 ? (
                                    <div className="space-y-3">
                                        {delegations.map((delegation) => (
                                            <div key={delegation.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {delegation.usuarioDelegado?.nombre} {delegation.usuarioDelegado?.apellido}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {delegation.usuarioDelegado?.email}
                                                        </p>
                                                        <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                                                            {delegation.motivo}
                                                        </p>
                                                        {delegation.fechaFin && (
                                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                Hasta: {new Date(delegation.fechaFin).toLocaleDateString()}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => handleRevokeDelegation(delegation.id)}
                                                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                                                    >
                                                        Revocar
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                        No hay delegaciones activas
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
